import {
  generateClientId,
  offlineDb,
  type OfflineDeviceLink,
} from "@/lib/offline/db";

/**
 * Shared-device → nursery link helpers (NAU-226).
 *
 * The link lives in a single-row Dexie table (`deviceLink`) that is excluded
 * from `clearAllOfflineData()`, so it survives logout / auto-logout. Only
 * `clearDeviceLink()` (the explicit reset-device flow) removes it.
 */
export const DEVICE_LINK_KEY = "device-link";

/** Generate the locally-stored device identifier. It names the device; the secret issued at link time is what authenticates it. */
export function generateDeviceUuid(): string {
  return generateClientId();
}

export async function getDeviceLink(): Promise<OfflineDeviceLink | undefined> {
  return offlineDb.deviceLink.get(DEVICE_LINK_KEY);
}

export async function isDeviceLinked(): Promise<boolean> {
  return (await getDeviceLink()) !== undefined;
}

export async function saveDeviceLink(
  data: Omit<OfflineDeviceLink, "id" | "cachedAt">,
): Promise<OfflineDeviceLink> {
  const record: OfflineDeviceLink = {
    id: DEVICE_LINK_KEY,
    cachedAt: Date.now(),
    ...data,
  };
  await offlineDb.deviceLink.put(record);
  return record;
}

/** Refresh the cached user list on the existing link. */
export async function updateCachedUsers(users: unknown): Promise<void> {
  await offlineDb.deviceLink.update(DEVICE_LINK_KEY, {
    cachedUsers: users,
    cachedAt: Date.now(),
  });
}

/**
 * Clear the device link. Called only by the explicit reset-device flow, AFTER
 * `clearAllOfflineData()` has wiped the rest of the caches (which leaves this
 * row intact by design — NAU-226 Decision 5).
 */
export async function clearDeviceLink(): Promise<void> {
  await offlineDb.deviceLink.delete(DEVICE_LINK_KEY);
}
